import { IMapDrawLayer, IMapGeometryLine, IMapGeometryPoint, IMapSettings } from "@components/map/map.settings";
import { chain } from "lodash";
import { IMapComponent } from "~app/common/components/map/map.models";
import { ITplArbetsorder, ITplDestinering, ITplHandelssortiment } from "../../models/dtos";
import { TplStateService } from "../../state/tpl-state.service";

export class TplLayerBinder {
    private currentBinding: IStateMapLayer;
    private readonly bound = new Map<string, IStateMapLayer>();
    private throttle: any;
    private gotoClaim = false;

    constructor(
        private readonly map: IMapComponent,
        private readonly mapsettings: IMapSettings,
        private readonly stateService: TplStateService) {
    }

    bind(statelayer: IStateMapLayer) {
        this.bound.set(statelayer.statename, statelayer);
        return this;
    }

    renderArbetsordrar(statename: string, goto = false) {
        if (!this.bound.has(statename)) {
            return;
        }
        this.gotoClaim = this.gotoClaim || goto;
        clearTimeout(this.throttle);
        this.throttle = setTimeout(() => {
            const aos = this.stateService.current.getArbetsordrar();
            this.currentBinding = this.bound.get(statename);
            if (statename === "omdestinera") {
                this.mapAsDestinationer(aos, this.gotoClaim);
            } else {
                this.mapAsDefaultPoint(aos, this.gotoClaim);
            }
            this.throttle = null;
            this.gotoClaim = false;
        }, 100);
    }

    renderCurrent() {
        this.renderArbetsordrar(this.currentBinding?.statename);
    }

    setFilterCurrent(ids: number[]) {
        const layers = this.currentBinding?.layers.map(x => x.layerid);
        this.map.filterGeometries(layers, ids);
    }

    setSelectedCurrent() {
        const arbetsorderIds = this.stateService.current.getSelected().selected;
        this.setSelected(arbetsorderIds);
    }

    setSelected(arbetsorderIds: number[]) {
        const candidates = [
            "arbetsordrar",
            "arbetsordrar-omdestinera",
            "arbetsordrar-neutral"
        ];
        this.map?.clearSelection();
        candidates.forEach(layerid => {
            this.map?.selectGeometries(layerid, arbetsorderIds);
        });
    }

    mapAsDefaultPoint(aos: ITplArbetsorder[], goto): void {
        const selected = this.stateService.current.getSelected().selected;
        const visible = this.stateService.current.getCurrentArbetsordrar();
        this.currentBinding.layers.forEach(layer => {
            const geometries = aos.map((ao: ITplArbetsorder) => ({
                id: ao.id,
                easting: ao.easting,
                northing: ao.northing,
                symbolStyle: this.getSymbolStyle(ao),
                color: layer.colorFunc ? layer.colorFunc(ao) : "white",
                iconid: layer.iconFunc ? layer.iconFunc(ao) : null,
                label: ao.beteckning,
                selected: selected.some(id => id === ao.id),
                tag: ao,
                visible: visible.some(vis => vis === ao)
            } as IMapGeometryPoint));
            this.refreshLayer(layer.layerid, geometries, goto);
        });
    }

    mapAsDestinationer(aos: ITplArbetsorder[], goto): void {
        const selected = this.stateService.current.getSelected().selected;
        const visible = this.stateService.current.getCurrentArbetsordrar();
        const handelssortiment = [...(this.stateService.current.getSelectedHandelssortiment() ?? [])];
        const mottagningsplatser = this.stateService.current.getSelectedMottagningsplatser() ?? [];

        const destineringHandelssortiment = this.stateService.current.getDestineringHandelssortiment();
        if (!!destineringHandelssortiment && handelssortiment.indexOf(destineringHandelssortiment) === -1) {
            handelssortiment.push(destineringHandelssortiment);
        }

        const hsFilterActive = handelssortiment?.length > 0;
        const matchHandelssortiment = (hs: ITplHandelssortiment): boolean => {
            if (!hsFilterActive) {
                return true;
            }
            return handelssortiment.includes(hs.handelssortimentId);
        };

        const mplFilterActive = mottagningsplatser?.length > 0;
        const matchMottagningsplats = (dest: ITplDestinering) => {
            if (!mplFilterActive) {
                return true;
            }
            return mottagningsplatser.includes(dest.mottagningsplatsId);
        };

        const isSelected = (ao: ITplArbetsorder): boolean => selected.includes(ao.id);

        const aoGeometries = this.mapArbetsorderGeometries(visible, matchHandelssortiment, isSelected);
        this.refreshLayer("arbetsordrar-omdestinera", aoGeometries, goto);

        const destineringGeometries = this.mapDestineringGeometries(visible, matchHandelssortiment, matchMottagningsplats);
        this.refreshLayer("destinationer", destineringGeometries, false);
    }

    private getSymbolStyle(ao: ITplArbetsorder): string {
        if(ao.handelssortiment.some(h => !!h.befraktareId)) {
            return "diamond";
        } else if(ao.isTerminalImport) {
            return "square";
        }
        return "circle";
    }

    private mapArbetsorderGeometries(
        arbetsorders: ITplArbetsorder[],
        hsFilter: (hs: ITplHandelssortiment) => boolean,
        isSelected: (ao: ITplArbetsorder) => boolean): IMapGeometryPoint[] {
        // ao-pluppar
        const aoPunkter = chain(arbetsorders)
            .map(x => {
                const isDestinerad = x.handelssortiment.filter(hsFilter).some(x1 => x1.destineringar.length > 0);
                const allDestinerad = x.handelssortiment.every(x1 => x1.destineringar.length > 0);
                const selected = isSelected(x);
                return {
                    id: x.id,
                    easting: x.easting,
                    northing: x.northing,
                    selected: selected,
                    symbolStyle: x.isTerminalImport ? "square" : "circle",
                    color: isDestinerad ? "green" : "white",
                    iconId: isDestinerad && !allDestinerad ? "deldestinerad" : null,
                    tag: x,
                    label: x.beteckning,
                    visible: true
                } as IMapGeometryPoint;
            })
            .value();
        // väglager-ikon
        const hasVaglager = (ao: ITplArbetsorder): boolean => ao.handelssortiment.filter(hsFilter).some(hs => hs.vagVolym > 0);
        const vaglagerPunkter: IMapGeometryPoint[] = aoPunkter
            .filter(aop => hasVaglager(aop.tag))
            .map(aop => ({
                id: aop.id,
                easting: aop.easting,
                northing: aop.northing,
                color: "white",
                iconid: "road",
                selected: false,
                symbolStyle: "circle",
                tag: aop.tag,
                label: "Väglager",
                visible: true,
                parent: aop
            }));

        return [
            ...aoPunkter,
            ...vaglagerPunkter
        ];
    }

    private mapDestineringGeometries(
        arbetsorders: ITplArbetsorder[],
        hsFilter: (hs: ITplHandelssortiment) => boolean,
        mplFilter: (dest: ITplDestinering) => boolean): IMapGeometryLine[] {

        return chain(arbetsorders)
            .flatMap(x => x.handelssortiment
                .filter(hsFilter)
                .map(hs => ({
                    destineringar: hs.destineringar
                        .filter(mplFilter)
                        .map(destinering => ({
                            id: x.id,
                            startEasting: x.easting,
                            startNorthing: x.northing,
                            endEasting: destinering.easting,
                            endNorthing: destinering.northing,
                            color: "brown",
                            visible: true,

                            label: null // TODO destinering.mottagningsplatsNamn..?
                        }))
                })))
            .flatMap(hs => hs.destineringar)
            .value();
    }

    private getLayer(layerid: string): IMapDrawLayer {
        return this.mapsettings.drawLayers.find(l => l.id === layerid);
    }

    private refreshLayer(layerid, geometries, goto): IMapDrawLayer {
        const layer = this.getLayer(layerid);
        if (!layer) {
            return undefined;
        }

        layer.geometries = geometries;
        this.map.refreshGeometries(layer.id);
        if (goto) {
            this.map.gotoAll([layer.id]);
        }
        return layer;
    }
}

export interface IStateMapLayer {
    statename: string;
    layers: IMapLayerItem[];
}

export interface IMapLayerItem {
    layerid: string;
    colorFunc?: (ao: ITplArbetsorder) => any;
    iconFunc?: (ao: ITplArbetsorder) => any;
}
